import {
  Box,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import {
  Event as EventIcon,
  VideoCameraFront,
  LocalHospital,
  LocationOn,
  Videocam,
  Chat as ChatIcon,
  Home,
  Close,
} from "@mui/icons-material";

import { useFormatter } from "../../utils/useFormatter";
import { useDatetime } from "../../utils/useDatetime";
import { useDict } from "../../utils/useDict";
import { useFhirData } from "../../utils/useFhirData";
import { useLocationContext } from "../../contexts/LocationContext";
import { ScheduleQueueActions } from "./ScheduleQueueActions";
import type { MessagingTemplate } from "src-next/modules/crm-clinical/messaging";

/** Tipos de atendimento reconhecidos */
type AttendanceKind = "presencial" | "teleatendimento" | "chat" | "domicilio";

/**
 * Detecta o tipo de atendimento a partir do recurso Location.
 * Usa a extension attendance-channel (novo) com fallback para virtual-service (legado).
 */
function getAttendanceKind(locationResource: any): AttendanceKind {
  const channel = locationResource?.extension?.find((ext: any) =>
    ext.url?.includes("attendance-channel"),
  )?.valueCode;

  const isVirtual =
    locationResource?.extension?.find((ext: any) =>
      ext.url?.includes("virtual-service"),
    )?.valueBoolean === true;

  if (channel === "chat") return "chat";
  if (channel === "domicilio") return "domicilio";
  if (isVirtual) return "teleatendimento";
  return "presencial";
}

/**
 * Ícone e cores por tipo — tokens do Theme.tsx, igual ao AttendanceTypeSelector e AccordionHeader.
 *   presencial      → primary   (teal)
 *   teleatendimento → secondary (azul)
 *   chat            → error     (vinho/roxo)
 *   domicilio       → warning   (amarelo)
 */
const ATTENDANCE_KIND_CONFIG: Record<
  AttendanceKind,
  { icon: React.ReactNode; colorKey: string; bgKey: string; label: string }
> = {
  presencial: {
    icon: <LocationOn sx={{ fontSize: 20 }} />,
    colorKey: "primary700.main",
    bgKey: "primary100.main",
    label: "Presencial",
  },
  teleatendimento: {
    icon: <Videocam sx={{ fontSize: 20 }} />,
    colorKey: "secondary700.main",
    bgKey: "secondary100.main",
    label: "Teleatendimento",
  },
  chat: {
    icon: <ChatIcon sx={{ fontSize: 20 }} />,
    colorKey: "error700.main",
    bgKey: "error100.main",
    label: "Chat",
  },
  domicilio: {
    icon: <Home sx={{ fontSize: 20 }} />,
    colorKey: "warning700.main",
    bgKey: "warning100.main",
    label: "Atendimento a Domicílio",
  },
};

interface PatientDetailsProps {
  data: (fhir5.Appointment | fhir5.Location | undefined)[];
  patient: fhir5.BundleEntry<fhir5.Patient> | undefined;
  setRefetch: any;
  onDeselectCard: () => void;
  handleCancelAppointment: any;
  /**
   * Templates já filtrados e consistentes para envio no WhatsApp (FluxZap).
   * Este valor deve vir do mesmo pipeline usado na lista (`ScheduleQueue`),
   * para evitar o modal abrir com templates diferentes ou vazios.
   */
  messagingTemplates: MessagingTemplate[];
  /**
   * Dados do tutor no modo vet (opcionais; usados pelo dialog/WhatsApp Web).
   */
  tutorPhone?: string;
  tutorCpf?: string;
}

export const PatientDetails: React.FC<PatientDetailsProps> = ({
  data,
  patient,
  setRefetch,
  onDeselectCard,
  handleCancelAppointment,
  messagingTemplates,
  tutorPhone,
  tutorCpf,
}) => {
  const { formatNameDisplay, formatDateTimeToHour } = useFormatter;

  const { formatDate } = useDatetime;

  const { location: locationContext } = useLocationContext();

  const appointment = data.find(
    (el) => el?.resourceType === "Appointment"
  ) as fhir5.Appointment;
  const location = data.find(
    (el) => el?.resourceType === "Location"
  ) as fhir5.Location;

  const locationAppointment = locationContext?.find(
    (loc) => loc?.resource?.id === location?.id
  );

  function formatAddress(location: any) {
    return (
      location?.address?.line?.join(", ") ||
      [
        location?.address?.city,
        location?.address?.state,
        location?.address?.postalCode,
      ]
        .filter((el) => el)
        .join(", ") ||
      location?.address?.country ||
      undefined
    );
  }

  const address = formatAddress(locationAppointment?.resource?.address);

  const locationTeleconsulting = useFhirData.virtualService(
    locationContext?.find((loc: any) =>
      loc?.resource?.id.includes(location?.id)
    )?.resource
  );

  // Tipo de atendimento detectado a partir da Location do agendamento
  const attendanceKind = getAttendanceKind(locationAppointment?.resource);
  const attendanceConfig = ATTENDANCE_KIND_CONFIG[attendanceKind];

  const appointmentContent = {
    status: appointment?.status,
    start: appointment?.start || "-",
    patient: patient?.resource?.name?.[0]?.text || "-",
    practitioner: useFhirData.findParticipant(
      appointment?.participant,
      "Practitioner"
    )?.display,
    location: useFhirData.findParticipant(appointment?.participant, "Location")
      ?.display,
    healthcare: useFhirData.findParticipant(
      appointment?.participant,
      "HealthcareService"
    )?.display,
    address: formatAddress(locationAppointment?.resource?.address),
    appointmentType: appointment?.appointmentType?.text
      ? useDict.appointmentType[appointment?.appointmentType?.text]
      : "-",
  };

  if (!appointment || !patient?.resource) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Selecione um paciente para ver os detalhes
        </Typography>
      </Box>
    );
  }

  const auxAppointment = {
    ...appointment,
    originalData: appointment,
  };

  return (
    <Box sx={{ p: 0, width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          // bgcolor: "primary.main",
          // color: "white",
        }}
      >
        {/* Patient Info Section */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack>
              <Avatar
                sx={{
                  width: 50,
                  height: 50,
                  bgcolor: "primary.light",
                  border: "2px solid",
                  borderColor: "primary.light",
                }}
              >
                {formatNameDisplay(patient.resource.name?.[0]?.text || "")?.[0]}
              </Avatar>
            </Stack>

            <Stack>
              <Typography variant="h5" sx={{ mb: 0.5 }}>
                {formatNameDisplay(patient.resource.name?.[0]?.text || "")}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {patient.resource.birthDate &&
                  `${formatDate(
                    patient.resource.birthDate
                  )} - ${useFormatter.formatAge(
                    patient.resource.birthDate
                  )} anos`}
              </Typography>
            </Stack>
          </Stack>

          <Tooltip title="Fechar detalhes">
            <IconButton onClick={onDeselectCard}>
              <Close />
            </IconButton>
          </Tooltip>
        </Stack>
        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            width: "100%",
          }}
        >
          <ScheduleQueueActions
            appointment={auxAppointment}
            patient={patient}
            setRefetch={setRefetch}
            handleCancelAppointment={handleCancelAppointment}
            locationTeleconsulting={!!locationTeleconsulting}
            locationResource={locationAppointment?.resource}
            variant="descriptive"
            messagingTemplates={messagingTemplates}
            tutorPhone={tutorPhone}
            tutorCpf={tutorCpf}
          />
        </Box>
        {/* Appointment Information Section */}
        <Stack
          direction="row"
          spacing={3}
          sx={{
            mt: 3,
            pt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            color: "primary",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventIcon sx={{ opacity: 0.7 }} />
            <Typography>
              {formatDateTimeToHour(appointment?.start || "")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VideoCameraFront sx={{ opacity: 0.7 }} />
            <Typography>
              {appointmentContent.appointmentType || "Não informado"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocalHospital sx={{ opacity: 0.7 }} />
            <Typography>
              {appointmentContent.healthcare || "Não informado"}
            </Typography>
          </Box>
          {appointmentContent.location && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Ícone colorido do tipo de atendimento — igual ao AccordionHeader */}
              <Tooltip title={attendanceConfig.label}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: attendanceConfig.bgKey,
                    color: attendanceConfig.colorKey,
                    borderRadius: "50%",
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                  }}
                >
                  {attendanceConfig.icon}
                </Box>
              </Tooltip>
              <Typography>{appointmentContent.location}</Typography>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};
